import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { EmployeeDocumentTable } from "@/components/crud/table/EmployeeDocumentTable";
import { EmployeeDocumentFormSheet } from "@/components/crud/forms/EmployeeDocumentFormSheet";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { toast } from "sonner";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import PageHeader from "@/components/reusable-components/header/page-header";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import { useDebounce } from "@/hooks/use-debounce";
import useFetchEmployeeDocuments, {
  type EmployeeDocumentOrdering,
} from "@/hooks/api/document/employee-document/useFetchEmployeeDocument";
import useCreateEmployeeDocument from "@/hooks/api/document/employee-document/useCreateEmployeeDocument";
import useUpdateEmployeeDocument from "@/hooks/api/document/employee-document/useUpdateEmployeeDocument";
import useDeleteEmployeeDocument from "@/hooks/api/document/employee-document/useDeleteEmployeeDocument";
import type {
  EmployeeDocument,
  EmployeeDocumentPayload,
} from "@/types/document";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

export const Route = createFileRoute("/admin/_layout/documents/employee")({
  component: EmployeeDocumentsPage,
});

function EmployeeDocumentsPage() {
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "document.employee.manage");
  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        title: SortDirection;
        created_at: SortDirection;
      },
      EmployeeDocumentOrdering
    >({
      title: null,
      created_at: null,
    });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editingDocument, setEditingDocument] =
    useState<EmployeeDocument | null>(null);
  const [documentToDelete, setDocumentToDelete] =
    useState<EmployeeDocument | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 500);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const employeeDocumentsQuery = useFetchEmployeeDocuments({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
  });
  const employeeDocuments = employeeDocumentsQuery.data?.results ?? [];
  const count = employeeDocumentsQuery.data?.count ?? 0;

  const createEmployeeDocument = useCreateEmployeeDocument();
  const updateEmployeeDocument = useUpdateEmployeeDocument();
  const deleteEmployeeDocument = useDeleteEmployeeDocument();

  const isMutating =
    createEmployeeDocument.isPending ||
    updateEmployeeDocument.isPending ||
    deleteEmployeeDocument.isPending;
  const isLoading = employeeDocumentsQuery.isPending || isMutating;
  const isFetching = employeeDocumentsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, setPage]);

  const totalPages = useMemo(() => {
    return count ? Math.ceil(count / pageSize) : 1;
  }, [count, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setOrdering({
      title: null,
      created_at: null,
    });
    setPage(1);
  };

  const handleCreate = () => {
    setEditingDocument(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (doc: EmployeeDocument) => {
    setEditingDocument(doc);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (doc: EmployeeDocument) => {
    setEditingDocument(doc);
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (doc: EmployeeDocument) => {
    setDocumentToDelete(doc);
    setIsDeleteDialogOpen(true);
  };

  const onCreate = async (data: EmployeeDocumentPayload) => {
    await createEmployeeDocument.mutateAsync(data);
    toast.success("Document created successfully!");
    setIsSheetOpen(false);
  };

  const onEdit = async (id: number, data: EmployeeDocumentPayload) => {
    await updateEmployeeDocument.mutateAsync({ id, payload: data });
    toast.success("Document updated successfully!");
    setIsSheetOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    try {
      await deleteEmployeeDocument.mutateAsync({ id: documentToDelete.id });
      toast.success("Document deleted successfully!");
      setIsDeleteDialogOpen(false);
      setDocumentToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Employee Documents"
          description="Manage documents specific to individual employees (e.g., contracts, IDs)."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={canManage ? handleCreate : undefined}
          addLabel="Add Document"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Search</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search documents..."
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </FiltersPanel>

        <EmployeeDocumentTable
          documents={employeeDocuments}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <EmployeeDocumentFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={() => setIsSheetOpen(false)}
          onCreate={onCreate}
          onEdit={onEdit}
          editingDocument={editingDocument}
          isLoading={isMutating}
        />

        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Document"
          description={`Are you sure you want to delete the document "${documentToDelete?.title}" for employee? This action cannot be undone.`}
          itemName={documentToDelete?.title || ""}
          isLoading={deleteEmployeeDocument.isPending}
          destructive={true}
        />
      </div>
    </SidebarInset>
  );
}
